import {
  type CustomType,
  deleteTeamSpace,
  TeamSpace,
  useInvalidateTeamSpaces,
} from "@prismicio/editor-fields"
import {
  Alert,
  Badge,
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  Separator,
  Text,
  useToast,
} from "@prismicio/editor-ui"
import { useMutation } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useSearchParams } from "react-router-dom"

import { HookFormCustomTypesField } from "../../app/components/hook-form/HookFormCustomTypesField"
import { HookFormInput } from "../../app/components/hook-form/HookFormInput"
import { env } from "../../app/env"
import { getRepositoryName } from "../../app/routing"
import { mainSpace, useTeamSpaces } from "../../app/services/role"
import { trackTeamSpaceDeleted } from "../../app/services/tracking"
import { useTeamSpaceNavigation } from "./TeamSpaces.utils"
import type { TeamSpaceFormSchema } from "./TeamSpacesDialog"

interface TeamSpacesDialogGeneralTabProps {
  teamSpace?: TeamSpace
  closeSettings: () => void
}

export function TeamSpacesDialogGeneralTab(
  props: TeamSpacesDialogGeneralTabProps,
) {
  const { teamSpace, closeSettings } = props

  const { control } = useFormContext<TeamSpaceFormSchema>()

  const teamSpaceNameByCustomTypeId = useTeamSpaceNameByCustomTypeId()

  const isMainSpace = teamSpace?.id === mainSpace.id

  return (
    <>
      <HookFormInput
        control={control}
        name="name"
        label="Space name"
        placeholder="e.g: Blog, Marketing..."
        description={
          isMainSpace
            ? "The Main Space exists by default and its name cannot be changed."
            : undefined
        }
        disabled={isMainSpace}
      />
      <Separator />
      <HookFormCustomTypesField
        control={control}
        name="customTypes"
        label="Manage your Space"
        description={
          isMainSpace
            ? "You can only preview Custom Types belonging to the Main Space here.\nTo manage them please visit the settings of your other Team Spaces."
            : "Select Custom Types you want assigned to this Space"
        }
        placeholder="Select types"
        customTypeItem={(customType) => {
          const teamSpaceName = teamSpaceNameByCustomTypeId.get(customType.id)
          return (
            <CustomTypeItem
              customType={customType}
              teamSpaceName={teamSpaceName}
            />
          )
        }}
        disabled={isMainSpace}
      />
      {teamSpace && !isMainSpace && (
        <DangerZone teamSpace={teamSpace} closeSettings={closeSettings} />
      )}
    </>
  )
}

interface CustomTypeItemProps {
  customType: CustomType
  teamSpaceName?: string
}

function CustomTypeItem(props: CustomTypeItemProps) {
  const { customType, teamSpaceName } = props
  return (
    <>
      <Badge
        title={customType.label}
        color="purple"
        size="medium"
        sx={{
          marginInline: 8,
          marginBottom: 4,
          marginTop: 4,
        }}
      />
      {teamSpaceName && (
        <Text variant="small" color="grey11">{`(${teamSpaceName})`}</Text>
      )}
    </>
  )
}

function useTeamSpaceNameByCustomTypeId(): Map<string, string> {
  const teamSpaces = useTeamSpaces()

  return useMemo(() => {
    const teamSpaceNameByCustomTypeId = new Map<string, string>()

    for (const teamSpace of teamSpaces) {
      for (const customType of teamSpace.customTypes) {
        teamSpaceNameByCustomTypeId.set(customType, teamSpace.name)
      }
    }

    return teamSpaceNameByCustomTypeId
  }, [teamSpaces])
}

interface DangerZoneProps {
  teamSpace: TeamSpace
  closeSettings: () => void
}

function DangerZone(props: DangerZoneProps) {
  const { teamSpace, closeSettings } = props

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

  return (
    <>
      <Separator />
      <Box flexDirection="column" gap={8}>
        <Text variant="bold">Danger Zone</Text>
        <Alert
          title="Delete this Space"
          subtitle="Once you delete a Space, there is no going back. Please be certain."
          rightButton={{
            type: "text-dimmed",
            title: "Delete this Space",
            onClick: () => setDeleteConfirmationOpen(true),
          }}
          color="error"
          align="center"
          noShadow
        />
      </Box>
      <TeamSpaceDeleteConfirmationDialog
        open={deleteConfirmationOpen}
        setOpen={setDeleteConfirmationOpen}
        teamSpace={teamSpace}
        closeSettings={closeSettings}
      />
    </>
  )
}

interface TeamSpaceDeleteConfirmationDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  teamSpace: TeamSpace
  closeSettings: () => void
}

function TeamSpaceDeleteConfirmationDialog(
  props: TeamSpaceDeleteConfirmationDialogProps,
) {
  const { open, setOpen, teamSpace, closeSettings } = props

  const { mutate: deleteTeamSpace, isPending: isDeleting } = useDeleteTeamSpace(
    {
      onSuccess: () => {
        setOpen(false)
        closeSettings()
      },
    },
  )

  return (
    <Dialog
      onOpenChange={(open) => {
        if (isDeleting) return
        setOpen(open)
      }}
      open={open}
      size="small"
      closeButtonDisabled={isDeleting}
    >
      <DialogHeader
        icon="alert"
        title="Do you really want to delete this Space?"
      />

      <DialogDescription asChild>
        <DialogContent>
          <Box padding={16}>
            <Text>You are about to delete the Space: {teamSpace.name}.</Text>
          </Box>
        </DialogContent>
      </DialogDescription>

      <DialogActions>
        <DialogCancelButton disabled={isDeleting}>No</DialogCancelButton>
        <DialogActionButton
          onClick={() => deleteTeamSpace(teamSpace)}
          loading={isDeleting}
          color="tomato"
        >
          Yes, I want to delete the Space
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

interface UseDeleteTeamSpaceArgs {
  onSuccess?: () => void
}

function useDeleteTeamSpace(args: UseDeleteTeamSpaceArgs) {
  const { onSuccess } = args

  const { showToast } = useToast()

  const invalidateTeamSpaces = useInvalidateTeamSpaces()

  const { navigateToTeamSpace } = useTeamSpaceNavigation()

  const [searchParams] = useSearchParams()

  return useMutation({
    mutationFn: (teamSpace: TeamSpace) =>
      deleteTeamSpace({
        baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
        repository: getRepositoryName(),
        authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        teamSpaceId: teamSpace.id,
      }),
    onError: () => {
      showToast({
        title: "Failed to delete the Space",
        icon: "block",
      })
    },
    onSuccess: async (_, deletedTeamSpace) => {
      if (searchParams.get("space") === deletedTeamSpace.id) {
        navigateToTeamSpace(mainSpace.id, deletedTeamSpace.customTypes)
      }

      await invalidateTeamSpaces()

      showToast({
        title: "Space deleted successfully",
      })

      onSuccess?.()

      trackTeamSpaceDeleted(deletedTeamSpace)
    },
  })
}
